// AppSections/AccommodationSection/AccommodationsSection.tsx
import React, { useState } from 'react';
import AccommodationCard from './AccommodationCard';
import CustomBooking from './CustomBooking';
import { Users } from 'lucide-react';

interface Bed {
  type: string;
  quantity: number;
}

interface Item {
  uid?: string;
  label: string;
  name: string;
  type?: string;
  capacity?: number;
  childCapacity?: number;
  beds?: Bed[];
  provider?: string;
  reservedDates?: string[];
  description?: string;
  image?: string;
  bgImage?: string;
  onImageButtons?: { label: string; value: string; url: string; style: string }[];
  details: { label: string; value: string; icon: string }[];
  rating?: number;
  pricing?: {
    tiers?: { [key: string]: number };
    seasonal?: {
      [key: string]: {
        dates: [string, string];
        multiplier: number;
        label?: string;
      };
    };
    date_specific?: Array<{
      date: string;
      price: number;
      label?: string;
    }>;
  };
}

interface AccommodationsSectionProps {
  data: {
    hashId: string;
    title: string;
    description: string;
    image: string;
    items: Item[];
  };
  onShowItem: (item: Item) => void;
  renderSectionHeader: (tag: string, title: string, description: string) => React.ReactNode;
  bookingEndpoint?: string;
}

const AccommodationsSection = ({ 
  data, 
  onShowItem, 
  renderSectionHeader,
  bookingEndpoint = '/api/accommodation-requests'
}: AccommodationsSectionProps) => {
  const [showCustomBooking, setShowCustomBooking] = useState(false);

  if (!data || !data.items || data.items.length === 0) return null;

  // Transform items to CustomBooking room format
  const transformRoomsForBooking = () => {
    return data.items.map(room => {
      // Calculate total beds
      const totalBeds = room.beds ? room.beds.reduce((sum, bed) => sum + bed.quantity, 0) : 1;
      
      // Get bed types as array
      const bedTypes = room.beds ? room.beds.map(b => b.type) : ['Standard Bed'];
      
      // Get bed quantities
      const bedQuantities = room.beds ? room.beds.map(b => b.quantity) : [1];
      
      return {
        id: room.uid || room.name,
        name: room.name,
        label: room.label || room.name,
        capacity: room.capacity || 2,
        beds: totalBeds,
        bedTypes: bedTypes,
        bedQuantities: bedQuantities,
        bedType: room.beds ? room.beds.map(b => `${b.quantity}x ${b.type}`).join(', ') : 'Standard Bed',
        price: room.pricing?.tiers ? Math.min(...Object.values(room.pricing.tiers)) : 100,
        provider: room.provider,
        description: room.description,
        image: room.bgImage || room.image,
        reservedDates: room.reservedDates,
        amenities: room.details?.map(d => d.value) || [],
        // Preserve original beds data for detailed display
        bedsData: room.beds || [],
      };
    });
  };

  const roomsForBooking = transformRoomsForBooking();

  return (
    <>
      <section
        id={data.hashId || 'accommodations'}
        className="relative py-20 overflow-hidden bg-gradient-to-b from-white to-blue-50"
      >
        <div className="container mx-auto max-w-7xl px-4">
          {renderSectionHeader(
            'Stay',
            data.title || 'Accommodations',
            data.description || 'Comfortable stays for every traveller'
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.items.map((room, index) => (
              <AccommodationCard
                key={room.uid || index}
                item={room}
                onShowItem={onShowItem}
              />
            ))}
          </div>

          {/* Custom Booking Button */}
          <div className="text-center mt-12">
            <button
              onClick={() => setShowCustomBooking(true)}
              className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
            >
              <Users className="w-5 h-5" />
              Custom Booking
              <span className="text-sm opacity-80">(Multiple Rooms & Guests)</span>
            </button>
            <p className="text-sm text-gray-500 mt-2">
              Need multiple rooms or have special requirements? Create a custom booking
            </p>
          </div>
        </div>
      </section>

      {/* Custom Booking Modal */}
      <CustomBooking
        isOpen={showCustomBooking}
        onClose={() => setShowCustomBooking(false)}
        rooms={roomsForBooking}
        endpoint={bookingEndpoint}
        title="Custom Accommodation Booking"
        subtitle="Select rooms and assign guests for your stay"
        submitLabel="Send Request"
        successMessage="Your custom booking request has been sent! We'll get back to you with available options."
        onSuccess={(data, response) => {
          console.log('Custom booking successful:', data, response);
        }}
        onError={(error) => {
          console.error('Custom booking failed:', error);
        }}
      />
    </>
  );
};

export default AccommodationsSection;